import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { FileText, Download } from 'lucide-react';
import { API_URL } from '../../utils/apiConfig';

/**
 * Benefits & Resources — reference documents for the employee's own company.
 *
 * Sits alongside the onboarding forms but is deliberately NOT one: there is no
 * completion state, no signature and no acknowledgement. An employee can open
 * or download these at any time, before or long after onboarding.
 *
 * The server scopes the list to the caller's own location, so Chipped Cup staff
 * see the Chipped Cup packet and Mess Hall staff see theirs. This component
 * renders whatever it is given and never asks for a company.
 *
 * Renders nothing at all when there are no resources, so the Forms page is
 * unchanged for any company that has not published one yet.
 *
 * `inline` drops the surrounding card for the desktop sidebar, which already
 * supplies its own Paper and padding.
 */
export default function BenefitsResources({ inline = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/benefits/resources`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`resources ${res.status}`);
        const data = await res.json();
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        // A resources failure must never take the forms page down with it —
        // onboarding is the critical path, this is reference material.
        console.error('Could not load benefits resources:', err);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const open = async (item) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/benefits/resources/${item.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`download ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Give the new tab time to take the blob before revoking it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error('Could not open resource:', err);
    }
  };

  if (loading || items.length === 0) return null;

  const body = (
    <>
      <Box sx={{ px: inline ? 0 : 2, pt: inline ? 0 : 2, pb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          Benefits &amp; Resources
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Reference documents for your store. Nothing to sign, open or download any time.
        </Typography>
      </Box>
      <List sx={{ pt: 0 }}>
        {items.map((item, i) => (
          <React.Fragment key={item.id}>
            <ListItem button onClick={() => open(item)} sx={{ py: 1.5, px: inline ? 1 : 2, borderRadius: inline ? 1 : 0 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <FileText size={22} color="#1976d2" />
              </ListItemIcon>
              <ListItemText
                primary={item.title}
                secondary={item.description || item.locationName}
                primaryTypographyProps={{ sx: { fontWeight: 500, fontSize: inline ? 14 : undefined } }}
              />
              <Download size={18} color="#c7c7cc" />
            </ListItem>
            {i < items.length - 1 && <Divider component="li" sx={{ ml: 7 }} />}
          </React.Fragment>
        ))}
      </List>
    </>
  );

  if (inline) {
    return (
      <Box sx={{ mt: 1 }}>
        <Divider sx={{ mb: 2 }} />
        {body}
      </Box>
    );
  }

  return (
    <Box sx={{ mx: 2, mb: 2 }}>
      <Paper sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        {body}
      </Paper>
    </Box>
  );
}
